/* eslint semi: 1 */
import { defineStore } from 'pinia';
import CONST from '@/globals/constants';

export type MediaOrientationDir = typeof CONST.ORIENTATION_VALUE_HORIZONTAL | typeof CONST.ORIENTATION_VALUE_VERTICAL;
export type MediaSortOrder = typeof CONST.MEDIA_SORT_ASC | typeof CONST.MEDIA_SORT_DESC;
export type MediaExtendedSortOrder = typeof CONST.MEDIA_SORT_ASC | typeof CONST.MEDIA_SORT_DESC | typeof CONST.MEDIA_SORT_NAME_ASC | typeof CONST.MEDIA_SORT_NAME_DESC;
export type MediaFileTypes = typeof CONST.MEDIA_TYPE_VALUE_IMAGE |
  typeof CONST.MEDIA_TYPE_VALUE_VIDEO |
  typeof CONST.MEDIA_TYPE_VALUE_AUDIO |
  typeof CONST.MEDIA_TYPE_VALUE_AUDIO_PLAYLIST |
  typeof CONST.MEDIA_TYPE_VALUE_PROJECT |
  typeof CONST.MEDIA_TYPE_VALUE_ELEMENTI;
export type MediaPageContextValue = typeof CONST.MEDIA_COMPONENT_AS_MEDIA_FILES |
  typeof CONST.MEDIA_COMPONENT_AS_ALERT_FILES |
  typeof CONST.MEDIA_COMPONENT_AS_LED_FILES |
  typeof CONST.MEDIA_COMPONENT_AS_VIDEOWALL_FILES;

export interface MediaQueryState {
  orientation: MediaOrientationDir;
  searchText: string;
  mediaTypes: MediaFileTypes[];
  isScheduled: boolean | null;
  mediaTags: string[];
  popIds: string[];
  ledTags: string[];
  modifiedAtFrom: string | null;
  modifiedAtTo: string | null;
  sortOrder: MediaExtendedSortOrder;
  mediaPageContext: MediaPageContextValue;
}

/**
 * Pinia store for all media API query parameters.
 *
 * Holds the current state of every filter/param that can be sent to the
 * `GET /media` endpoint. This is the single reactive source of truth for
 * the query — UI components and composables read from and write to this store.
 *
 * Persistence (URL query params + localStorage) is handled one layer up in
 * `useMediaQuery` via `$filtersRepository`.
 *
 * Note on `isScheduled` vs `mediaTypes`:
 *   The legacy `$filtersRepository` stores the scheduled filter by embedding
 *   `'scheduled'` inside the `mediaTypes` array. This store cleanly separates
 *   the two concerns. `useMediaQuery.initialize()` handles the split when
 *   hydrating from the repository, and `useMediaQuery.applyIsScheduled()`
 *   handles the merge when persisting back.
 */
export const useMediaQueryStore = defineStore('media-query-store', {
  state: (): MediaQueryState => ({
    orientation: CONST.ORIENTATION_VALUE_HORIZONTAL,
    searchText: '',
    /**
     * Active media type tokens. Never contains `'scheduled'` — that lives in
     * `isScheduled`. Maps to the `?type=` API param.
     * @type {string[]}
     */
    mediaTypes: [],
    /**
     * Scheduled filter state. Maps to the `?scheduled=` API param.
     * - `true`  → only scheduled items
     * - `false` → only non-scheduled items
     * - `null`  → no filter applied
     * @type {boolean | null}
     */
    isScheduled: null,
    mediaTags: [],
    popIds: [],
    /**
     * For regular media pages this is always [].
     * For LED/Videowall pages it holds the active resolution tag e.g. ['1920x1080'].
     * @type {string[]}
     */
    ledTags: [],

    /**
     * DateTime ISODate format for start of the `modifiedAt`
     * range filter. To be converted to ISO 8601 string before sending to API.
     * Maps to the `?modifiedAtFrom=` API param.
     * @type {string | null}
     */
    modifiedAtFrom: null,
    /**
     * DateTime ISODate format for end of the `modifiedAt`
     * range filter. To be converted to ISO 8601 string before sending to API.
     * Maps to the `?modifiedAtTo=` API param.
     * @type {string | null}
     */
    modifiedAtTo: null,
    /**
     * Sort order token in `field:direction` format.
     * Maps to the `?sortOrder=` API param. Defaults to `'desc'` (newest first).
     * Valid values: `'desc'`, `'asc'`.
     * `modifiedAtFrom`/`modifiedAtTo` are only valid when sort field is `modifiedAt`.
     * @type {string}
     */
    sortOrder: CONST.SORT_ORDER_DEFAULT,

    /**
     * Page-type context set once by the host page component.
     * One of the `MEDIA_COMPONENT_AS_*` constants. Stored as a single string
     * to make invalid states unrepresentable (vs. three independent booleans).
     * NOT reset by `reset()` — tied to component lifecycle, not filter state.
     * The host page must call `setPageContext()` on setup and clear it on unmount.
     * @type {string}
     */
    mediaPageContext: CONST.MEDIA_COMPONENT_AS_MEDIA_FILES
  }),
  getters: {
    /** True when orientation is set to vertical. */
    isOrientationVertical: (state) => state.orientation === CONST.ORIENTATION_VALUE_VERTICAL,

    /** Derived from `mediaPageContext` — true when this is a Media page. */
    isFiles: (state) => state.mediaPageContext === CONST.MEDIA_COMPONENT_AS_MEDIA_FILES,

    /** Derived from `mediaPageContext` — true when this is an LED page. */
    isLed: (state) => state.mediaPageContext === CONST.MEDIA_COMPONENT_AS_LED_FILES,

    /** Derived from `mediaPageContext` — true when this is a Videowall page. */
    isVideowall: (state) => state.mediaPageContext === CONST.MEDIA_COMPONENT_AS_VIDEOWALL_FILES,

    /** Derived from `mediaPageContext` — true when this is an Alert page. */
    isAlert: (state) => state.mediaPageContext === CONST.MEDIA_COMPONENT_AS_ALERT_FILES,

    /**
     * True when any list-level filter is active.
     * Excludes UI-only query facets (`mediaTags`, `popIds`, `ledTags`).
     */
    hasActiveFilters: (state) => (
      !!state.searchText ||
      state.mediaTypes.length > 0 ||
      state.isScheduled !== null ||
      state.modifiedAtFrom !== null ||
      state.modifiedAtTo !== null
    ),
    /**
     * True when at least one query param deviates from its default empty value.
     * Orientation is intentionally excluded — it always has a value.
     */
    hasActiveQuery: (state) => (
      !!state.searchText ||
      state.mediaTypes.length > 0 ||
      state.isScheduled !== null ||
      state.mediaTags.length > 0 ||
      state.popIds.length > 0 ||
      state.ledTags.length > 0 ||
      state.modifiedAtFrom !== null ||
      state.modifiedAtTo !== null
    )
  },
  actions: {
    setOrientation (value: MediaOrientationDir) {
      this.orientation = value;
    },
    setSearchText (value: string) {
      this.searchText = value;
    },
    setMediaTypes (types: MediaFileTypes[]) {
      this.mediaTypes = types;
    },
    setIsScheduled (value: boolean | null) {
      this.isScheduled = value;
    },
    setMediaTags (tags: string[]) {
      this.mediaTags = tags;
    },
    setPopIds (ids: string[]) {
      this.popIds = ids;
    },
    setLedTags (tags: string[]) {
      this.ledTags = tags;
    },
    setModifiedAtFrom (value: string | null) {
      this.modifiedAtFrom = value;
    },
    setModifiedAtTo (value: string | null) {
      this.modifiedAtTo = value;
    },
    setSortOrder (value: MediaExtendedSortOrder) {
      this.sortOrder = value;
    },
    /**
     * Sets the page-type context that controls which API params are emitted
     * and which repository keys are used for LED/Videowall tags.
     * Call once from the host page component on setup; not reset by `reset()`.
     */
    setPageContext (value: MediaPageContextValue) {
      this.mediaPageContext = value;
    },
    setPatch (patch: Partial<MediaQueryState>) {
      this.$patch(patch);
    },

    /**
     * Resets all query params to their default empty values.
     * Does NOT touch `$filtersRepository` — use `clearAll()` in `useMediaQuery`
     * if you also want to flush URL params and localStorage.
     */
    reset () {
      this.$patch({
        orientation: CONST.ORIENTATION_VALUE_HORIZONTAL,
        searchText: '',
        mediaTypes: [],
        isScheduled: null,
        mediaTags: [],
        popIds: [],
        ledTags: [],
        modifiedAtFrom: null,
        modifiedAtTo: null,
        sortOrder: CONST.SORT_ORDER_DEFAULT
      });
    }
  }
});
